import {useRef, useState, useEffect} from 'react'
import {Formik, Form, Field, ErrorMessage, FormikProps} from 'formik'
import * as Yup from 'yup'
import 'bootstrap/dist/css/bootstrap.min.css'
import Astericsign from './Astericsign'
import {emailValidation} from './helper/ValidationHelperComponent'
import PrimaryButton from '../common/buttons/PrimaryButton'
import { StaffCategoryModel } from '../../../../entities/entities'

interface FormValues {
  email: string
  name: string
  category: string // Optional field for staff category
}

interface CreateUserFromProps {
  title?: string
  isOpen: boolean
  isInvitingStaff?: boolean
  staffTypes?: StaffCategoryModel[]
  onClose: () => void
  isLoading: boolean
  handleSubmit: (values: any, resetForm: () => void) => void
  setFormDirty?: (isDirty: boolean) => void
  shouldReset?: boolean
}

const validationSchema = Yup.object().shape({
  email: emailValidation,
  name: Yup.string().min(2).max(55).required('Name is required'),
  category: Yup.string().required('Role is required'), // Validation for staff category
})

function CreateUserFrom({
  title,
  isOpen,
  onClose,
  staffTypes,
  isLoading = false,
  isInvitingStaff= false,
  handleSubmit,
  setFormDirty,
  shouldReset = false,
}: CreateUserFromProps) {
  const resetFormRef = useRef<(() => void) | null>(null) // Store resetForm function
  const formikRef = useRef<FormikProps<FormValues>>(null)

  // State to preserve form values between renders
  const [formValues, setFormValues] = useState<FormValues>({
    email: '',
    name: '',
    category: '',
  })

  // Reset form when shouldReset changes to true
  useEffect(() => {
    if (shouldReset) {
      setFormValues({email: '', name: '',category: ''}) // Reset form values
      if (resetFormRef.current) {
        resetFormRef.current()
      }
    }
  }, [shouldReset])

  // initial values
  const initialValues: FormValues = {
    email: formValues.email,
    name: formValues.name,
    category: formValues.category || '',
  }

  const handleClose = () => {
    onClose() // Close modal
  }


  // Form submission handler
  const handleFormSubmit = (values: FormValues, {setSubmitting, resetForm}: any) => {
    // Ensure email is lowercase
    const normalizedValues = {
      ...values,
      email: values.email.toLowerCase().trim(),
    }

    handleSubmit(normalizedValues, () => {
      resetForm()
      setFormValues({email: '', name: '', category: ''}) // Clear state values after submission
    })
    if (setFormDirty) setFormDirty(false)
    setSubmitting(false)
  }

  return (
    <>
      {/* modal overlay */}
      {isOpen && (
        <div
          onClick={handleClose}
          className='modal-backdrop show'
          style={{backgroundColor: 'rgba(0, 0, 0, 0.5)'}}
        />
      )}

      <div className={`modal fade ${isOpen ? 'show d-block' : 'd-none'}`} tabIndex={-1}>
        <div className='modal-dialog modal-dialog-centered'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>{title}</h5>
              <button type='button' className='btn-close' onClick={handleClose}></button>
            </div>
            <div className='modal-body mb-0'>
              <Formik
                initialValues={initialValues}
                enableReinitialize={false} // Disable reinitialization to preserve values
                validationSchema={validationSchema}
                validateOnBlur={false}
                onSubmit={handleFormSubmit}
                innerRef={formikRef as any}
              >
                {({isSubmitting, resetForm, values, dirty}) => {
                  resetFormRef.current = resetForm // Store resetForm in ref

                  // Update parent component about form dirty state when it changes
                  if (setFormDirty && dirty !== undefined) {
                    setFormDirty(dirty)
                  }

                  return (
                    <Form
                      onChange={() => {
                        if (setFormDirty) setFormDirty(true)
                        // Update form values for persistence when they change
                        if (values && (values.name || values.email)) {
                          setFormValues(values)
                        }
                      }}
                    >
                      <div className='mb-3'>
                        <label className='form-label'>
                          Name <Astericsign />
                        </label>
                        <Field name='name' type='text' className='form-control' />
                        <ErrorMessage name='name' component='div' className='text-danger' />
                      </div>
                      <div className='mb-3'>
                        <label className='form-label'>
                          Email <Astericsign />
                        </label>
                        <Field name='email' type='email' className='form-control' />
                        <ErrorMessage name='email' component='div' className='text-danger' />
                      </div>
                      {(isInvitingStaff && staffTypes ) && (
                      <div className='mb-3'>
                          <label className='form-label'>
                            Role <Astericsign />
                          </label>
                          <Field as='select' name='category' className='form-select'>
                            <option value=''>Select staff role</option>
                            {staffTypes?.map((d) => (
                              <option key={d.id} value={d.id}>
                                {d.name}
                              </option>
                            ))}
                          </Field>
                          <ErrorMessage
                            name='category'
                            component='div'
                            className='text-danger mt-1'
                          />
                        </div>
                      )  
                      }


                      <div className='d-flex justify-content-end align-items-center'>
                        <PrimaryButton className='' disabled={isLoading} type='submit'>
                          {!isLoading && <span className='indicator-label'>Invite</span>}
                          {isLoading && (
                            <span className='indicator-progress' style={{display: 'block'}}>
                              Inviting...
                              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                            </span>
                          )}
                        </PrimaryButton>
                      </div>
                    </Form>
                  )
                }}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default CreateUserFrom
